import React, { createContext, useContext, useState, useCallback } from "react";
import {fetchProduct, fetchUnitsOfMeasure, addUnitOfMeasure, updateUnitOfMeasure, deleteUnitOfMeasure, fetchProductsForPurchase,
    addProduct, updateProduct, deleteProduct} from "./api/api";
// import { useUser } from "./UserContext";
import { useAuthenticatedEffect } from "./components/useauthenticatedeffect";

const ProductContext = createContext();

export const useProducts = () => useContext(ProductContext);

export const ProductProvider = ({children}) => {
    const [products, setProducts] = useState([]);
    const [productsForPurchase, setProductsForPurchase] = useState([]);
    const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
    // const { user } = useUser();
    const [totalPages, setTotalPages] = useState(0);

    const entriesPerPage = 20;

    const [totalProductsCount, setTotalProductsCount] = useState(0);

    // added
    const fetchAllProducts = useCallback(async (page = 1, searchTerm = '', categoryFilter = '', stockPositive = false) => {
        try {
            const data = await fetchProduct(page, searchTerm, categoryFilter, stockPositive);

            // console.log('API Response:', data);

            if (data && data.results) {
                setProducts(data.results);
                setTotalPages(Math.ceil(data.count / entriesPerPage));
            } else if (Array.isArray(data)) {
                setProducts(data);
                setTotalPages(1);
            } else {
                setProducts([]);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Error fetching products from context:', error);
        }
    }, []);

    // to get total products count
    const fetchTotalProductsCount = useCallback(async () => {
        try {
            const data = await fetchProduct(1); // fetch first page to get total count

            if (data && typeof data.count === 'number') {
                setTotalProductsCount(data.count);
            } else {
                setTotalProductsCount(0);
            }
        } catch (error) {
            console.error('Error fetching total products count:', error);
        }
    }, []);

    // to get total stock value
    const fetchAllProductsForStockValue = useCallback(async () => {
        try {
            let allProducts = [];
            let page = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const data = await fetchProduct(page);

                if (data && data.results) {
                    allProducts = [...allProducts, ...data.results];

                    if (data.next) {
                        page += 1;
                    } else {
                        hasNextPage = false;
                    }
                } else {
                    hasNextPage = false;
                }
            }

            return allProducts;
        } catch (error) {
            console.error('Error fetching all products for stock value:', error);
            return [];
        }
    }, []);

    // to get recently added products
    const fetchAllProductsForRecent = useCallback(async () => {
        try {
            let allProducts = [];
            let page = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const data = await fetchProduct(page);

                if (data && data.results) {
                    allProducts = [...allProducts, ...data.results];

                    if (data.next) {
                        page += 1;
                    } else {
                        hasNextPage = false;
                    }
                } else {
                    hasNextPage = false;
                }
            }

            return allProducts;
        } catch (error) {
            console.error('Error fetching all products for recent products:', error);
            return [];
        }
    }, []);

    // low stock products
    const fetchAllProductsForLowStock = useCallback(async () => {
        try {
            let allProducts = [];
            let page = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const data = await fetchProduct(page);

                if (data && data.results) {
                    // filter out is_active=false products
                    const activeResults = data.results.filter(product => product.is_active);

                    allProducts = [...allProducts, ...activeResults];

                    if (data.next) {
                        page += 1;
                    } else {
                        hasNextPage = false;
                    }
                } else {
                    hasNextPage = false;
                }
            }

            return allProducts;
        } catch (error) {
            console.error('Error fetching all products for low stock:', error);
            return [];
        }
    }, []);


    // use AuthenticatedEffect instead of useEffect for fetchTotalProductsCount
    useAuthenticatedEffect(
        useCallback(() => {
            fetchTotalProductsCount().then(r => r); // added then(r => r) to remove warning
        }, [fetchTotalProductsCount])
    );


    const fetchAllUnitsOfMeasure = useCallback(async () => {
        try {
            const unitsOfMeasureData = await fetchUnitsOfMeasure();
            setUnitsOfMeasure(unitsOfMeasureData);
        } catch (error) {
            console.error('Error fetching units of measure from context:', error);
        }
    }, []);

    const addUnitOfMeasureContext = useCallback(async (newUnit) => {
        try {
            const addedUnit = await addUnitOfMeasure(newUnit);
            setUnitsOfMeasure([...unitsOfMeasure, addedUnit]);
        } catch (error) {
            console.error('Error adding unit of measure from context:', error);
        }
    }, [unitsOfMeasure]);

    // useEffect(() => {
    //     fetchAllUnitsOfMeasure().then(r => r); // added then(r => r) to remove warning
    // }, [fetchAllUnitsOfMeasure]);

    const updateUnitOfMeasureContext = useCallback(async (unitId, updatedUnit) => {
        try {
            const updatedUnitData = await updateUnitOfMeasure(unitId, updatedUnit);
            setUnitsOfMeasure((prevUnits) =>
                prevUnits.map((unit) =>
                    unit.id === unitId ? updatedUnitData : unit));
        } catch (error) {
            console.error('Error updating unit of measure from context:', error);
        }
    }, []);

    const deleteUnitOfMeasureContext = useCallback(async (unitId) => {
        try {
            await deleteUnitOfMeasure(unitId);
            setUnitsOfMeasure((prevUnits) => prevUnits.filter(unit => unit.id !== unitId));
        } catch (error) {
            console.error('Error deleting unit of measure:', error);
        }
    }, []);

    const fetchAllProductsForPurchase = useCallback(async () => {
        try {
            const productData = await fetchProductsForPurchase();
            setProductsForPurchase(productData);
        } catch (error) {
            console.error('Error fetching products for purchase from context:', error);
        }
    }, []);

    const handleAddProduct = async (newProduct) => {
        try {
            const addedProduct = await addProduct(newProduct);
            setProducts([...products, addedProduct]);
        } catch (error) {
            console.error('Error adding product from context:', error);
        }
    };

    const handleUpdateProduct = async (updatedProduct) => {
        try {
            const updatedProductData = await updateProduct(updatedProduct);
            setProducts((currentProducts) =>
                currentProducts.map((product) =>
                    product.id === updatedProductData.id ? updatedProductData : product));
        } catch (error) {
            console.error('Error updating product from context:', error);
        }
    };

    const handleDeleteProduct = async (productId) => {
        try {
            await deleteProduct(productId);
            setProducts(products.filter(p => p.id !== productId));
        } catch (error) {
            console.error('Error deleting product:', error);
        }
    };

    const updateProductsForEditedCategory = (updatedCategory) => {
        setProducts(products.map(product => {
            if (product.category && product.category.id === updatedCategory.id) {
                return {...product, category_name: updatedCategory.category_name};
            }
            return product;
        }));
    };

    const updateProductsForDeletedCategory = (categoryId) => {
        setProducts(products.map(product => {
            if (product.category && product.category.id === categoryId) {
                return {...product, category_name: 'Uncategorized'};
            }
            return product;
        }));
    }

    // use useAuthenticatedEffect instead of useEffect for fetchAllUnitsOfMeasure
    useAuthenticatedEffect(
        useCallback(() => {
            fetchAllUnitsOfMeasure().then(r => r); // added then(r => r) to remove warning
        }, [fetchAllUnitsOfMeasure])
    );

    // use useAuthenticatedEffect instead of useEffect for fetchAllProducts
    useAuthenticatedEffect(
        useCallback(() => {
            fetchAllProducts().then(r => r); // added then(r => r) to remove warning
        }, [fetchAllProducts])
    );

    return (
        <ProductContext.Provider value={{
            products,
            productsForPurchase,
            fetchAllProducts,
            totalPages,
            totalProductsCount,
            fetchAllProductsForStockValue,
            fetchAllProductsForRecent,
            fetchAllProductsForLowStock,
            fetchAllProductsForPurchase,
            unitsOfMeasure,
            fetchAllUnitsOfMeasure,
            addUnitOfMeasure: addUnitOfMeasureContext,
            updateUnitOfMeasureContext,
            deleteUnitOfMeasureContext,
            addProduct: handleAddProduct,
            updateProduct: handleUpdateProduct,
            deleteProduct: handleDeleteProduct,
            updateProductsForEditedCategory,
            updateProductsForDeletedCategory
        }}>
            {children}
        </ProductContext.Provider>
    )
}